import { Document, Text, View, Image, Font } from "@react-pdf/renderer";
import CfmReportStyles from "./CfmReportStyles";
import PersonalDetailsSection from "./Pages/Details/PersonalDetailsSection";
import PensionResultsSection from "./Pages/Calculations/PensionResultsSection";
import InvestmentResultsSection from "./Pages/Calculations/InvestmentResultsSection";
import IncomeExpenditureResultsSection from "./Pages/Calculations/IncExpResultsSection";
import PageWrapper, { resetPageCounter } from "./Components/PageWrapper";
import CombinedDetailsSection from "./Pages/Details/CombinedDetails";

Font.registerHyphenationCallback((word) => [word]);

const CfmReport = ({
  state,
  tradingName,
  primaryColor,
  logo,
  userName,
  userEmail,
  regulatoryText,
  pensionChartImages,
  investmentChartImages,
  incExpdChartImages,
  surDefChartImages,
  stackedPenChartImages,
  stackedInvChartImages,
  combinedIncExpdChartImages,
  combinedSurDefChartImages,
  includeTables = true, // New prop to control table inclusion
}) => {
  const styles = CfmReportStyles(primaryColor);
  resetPageCounter();
  const pensionResults = state.pensionResults || [];
  const investmentResults = state.investmentResults || [];
  const incomeResults = state.incomeExpenditureResults || [];
  const details = state.details || [];

  // console.log(pensionChartImages);
  // console.log("CfmReport: Rendering report with state:", state);
  // console.log(pensionChart);
  return (
    <Document>
      {/* Cover Page */}
      <PageWrapper
        size="A4"
        orientation="landscape"
        style={styles.page}
        tradingName={tradingName}
        primaryColor={primaryColor}
      >
        <View style={styles.graphicWrapper}>
          <View style={styles.graphicBox} />
          <View style={styles.graphicBox1} />
          <View style={styles.graphicBox2} />
          <View style={styles.graphicBox3} />
        </View>
        <View
          style={{
            ...styles.overlayContent,
            paddingLeft: 60,
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <View style={styles.column}>
            <Text style={styles.title}>Cashflow</Text>
            <Text style={styles.title}>Modeller</Text>
            <Text style={styles.title}>Report</Text>
          </View>
          <Text style={styles.tradingNameTitle}>{tradingName}</Text>
          <View style={{ height: 6 }} />
          <View style={styles.detailContainer}>
            <Text style={styles.coverPageSubTitle}>Prepared By: </Text>
            <Text style={styles.tradingNameTitle1}>{userEmail}</Text>
          </View>
          <View style={styles.detailContainer}>
            <Text style={styles.coverPageSubTitle}>Prepared for:</Text>
            <Text style={styles.tradingNameTitle1}>
              {details
                ?.map((d) => d?.name)
                .filter(Boolean)
                .join(" & ") || "Client Name"}
            </Text>
          </View>
          <View style={styles.detailContainer}>
            <Text style={styles.coverPageSubTitle}>Date:</Text>
            <Text style={styles.tradingNameTitle1}>
              {new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </Text>
          </View>
          <View style={{ width: "45%" }}>
            <Text style={styles.disclaimerText}>
              Unforeseen events and changes in your circumstances will degrade
              the accuracy of this projection as time progresses. This report
              should therefore be recalculated every 12 months, based on your
              latest known requirements, fund values and economic data. Please
              speak to your adviser.
            </Text>
          </View>
        </View>
        {logo && (
          <View style={styles.logoContainer}>
            <Image style={styles.logoImage} src={logo} />
          </View>
        )}
      </PageWrapper>

      {/* <View style={styles.pageBreak} /> */}
      {/* <Image
        // style={styles.headerImage}
        style={styles.chartImage}
        src={pensionChart}
      /> */}

      {/* Overview and Summary Sections */}
      {details.map((person, index) => {
        const personPensions =
          state.pensions?.filter((p) => p.name === person.name) || [];
        const personInvestments =
          state.investments?.filter((i) => i.name === person.name) || [];
        const personIncome =
          state.income?.filter((inc) => inc.name === person.name) || [];
        const personRegularIncome =
          state.regularIncome?.filter((ri) => ri.name === person.name) || [];
        const personDrawdowns =
          state.drawdowns?.filter((d) => d.name === person.name) || [];
        const personAdHoc =
          state.adHocData?.filter((a) => a.name === person.name) || [];

        // const personChartImage = state.surpluDeficitChartImages?.[person.name];

        // console.log(personChartImage);

        return (
          <>
            <PageWrapper
              key={`overview-${index}`}
              // size={{ width: 1440, height: 912 }}
              size="A4"
              orientation="landscape"
              style={styles.page}
              tradingName={tradingName}
              primaryColor={primaryColor}
              headerTitle={`${person.name} - Financial Overview and Assumptions`}
              headerSubText={""}
              footerText={regulatoryText}
            >
              <PersonalDetailsSection
                state={person}
                primaryColor={primaryColor}
                inflation={state.inflation}
                dropRiskData={state.dropRiskData || []}
                pensions={personPensions}
                investments={personInvestments}
                otherIncomeSources={personIncome}
                notes={state.notes}
              />
            </PageWrapper>
            <PageWrapper
              key={`${person.name}-financial-summary`}
              size="A4"
              style={styles.page}
              orientation="landscape"
              tradingName={tradingName}
              primaryColor={primaryColor}
              headerTitle={`${person.name} - Financial Summary`}
              headerSubText={""}
              footerText={regulatoryText}
            >
              <CombinedDetailsSection
                state={{ name: person.name }}
                drawdowns={personDrawdowns}
                adHoc={personAdHoc}
                primaryColor={primaryColor}
                personPensions={personPensions}
                personInvestments={personInvestments}
                personRegularIncome={personRegularIncome}
                personIncome={personIncome}
                pensionResults={pensionResults.filter(
                  (res) => res.name === person.name
                )}
              />
            </PageWrapper>

            {personPensions.length > 0 && pensionChartImages[person.name] && (
              <PageWrapper
                key={`${person.name}-pension-chart`}
                size="A4"
                style={styles.page}
                orientation="landscape"
                tradingName={tradingName}
                primaryColor={primaryColor}
                headerTitle={`${
                  details.length > 1 ? `${person.name} - ` : ""
                }Pension Graph`}
                footerText={regulatoryText}
              >
                <View
                  style={{
                    ...styles.section,
                    gap: 0,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Text style={styles.disclaimerText1}>
                    This graph shows the pension increasing with growth and
                    contributions, and the expected drop in value when you start
                    taking money out.
                  </Text>
                  <Image
                    style={{
                      ...styles.chartImage,
                    }}
                    src={pensionChartImages[person.name]}
                  />
                  {state.graphNotes?.[person.name]?.["Pension"]?.trim?.() && (
                    <Text
                      style={{
                        ...styles.disclaimerText1,
                        ...styles.remarksContainer,
                      }}
                    >
                      <Text style={styles.textBold}>Comments: </Text>
                      {state.graphNotes?.[person.name]?.["Pension"] || ""}
                    </Text>
                  )}
                </View>
              </PageWrapper>
            )}

            {personInvestments.length > 0 &&
              investmentChartImages[person.name] && (
                <PageWrapper
                  key={`${person.name}-investment-chart`}
                  size="A4"
                  style={styles.page}
                  orientation="landscape"
                  tradingName={tradingName}
                  primaryColor={primaryColor}
                  headerTitle={`${
                    details.length > 1 ? `${person.name} - ` : ""
                  }Investment Graph`}
                  footerText={regulatoryText}
                >
                  <View
                    style={{
                      ...styles.section,
                      gap: 0,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Text style={styles.disclaimerText1}>
                      This graph shows how your investments grow over time and
                      helps track whether you’re on target to meet your goals.
                    </Text>
                    <Image
                      style={styles.chartImage}
                      src={investmentChartImages[person.name]}
                    />
                    {state.graphNotes?.[person.name]?.[
                      "Investment"
                    ]?.trim?.() && (
                      <Text
                        style={{
                          ...styles.disclaimerText1,
                          ...styles.remarksContainer,
                        }}
                      >
                        <Text style={styles.textBold}>Comments: </Text>
                        {state.graphNotes?.[person.name]?.["Investment"] || ""}
                      </Text>
                    )}
                  </View>
                </PageWrapper>
              )}

            {incExpdChartImages[person.name] && (
              <PageWrapper
                key={`${person.name}-income-chart`}
                size="A4"
                style={styles.page}
                orientation="landscape"
                tradingName={tradingName}
                primaryColor={primaryColor}
                headerTitle={`${
                  details.length > 1 ? `${person.name} - ` : ""
                }Income & Expenditure Graph`}
                footerText={regulatoryText}
              >
                <View
                  style={{
                    ...styles.section,
                    gap: 0,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Text style={styles.disclaimerText1}>
                    This bar chart shows your expected income sources and lump
                    sum payments, with your likely expenditure represented by
                    the line.
                  </Text>
                  <Image
                    style={styles.chartImage}
                    src={incExpdChartImages[person.name]}
                  />
                  {state.graphNotes?.[person.name]?.[
                    "Income & Expenditure"
                  ]?.trim?.() && (
                    <Text
                      style={{
                        ...styles.disclaimerText1,
                        ...styles.remarksContainer,
                      }}
                    >
                      <Text style={styles.textBold}>Comments: </Text>
                      {state.graphNotes?.[person.name]?.[
                        "Income & Expenditure"
                      ] || ""}
                    </Text>
                  )}
                </View>
              </PageWrapper>
            )}

            {surDefChartImages[person.name] && (
              <PageWrapper
                key={`${person.name}-surplus-chart`}
                size="A4"
                style={styles.page}
                orientation="landscape"
                tradingName={tradingName}
                primaryColor={primaryColor}
                headerTitle={`${
                  details.length > 1 ? `${person.name} - ` : ""
                }Surplus & Deficit Graph`}
                footerText={regulatoryText}
              >
                <View
                  style={{
                    ...styles.section,
                    gap: 0,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Text style={styles.disclaimerText1}>
                    The graph shows your income versus spending, highlighting
                    any surplus (excess funds) or deficit (shortfall).
                  </Text>
                  <Image
                    style={styles.chartImage}
                    src={surDefChartImages[person.name]}
                  />
                  {state.graphNotes?.[person.name]?.[
                    "Surplus & Deficit"
                  ]?.trim?.() && (
                    <Text
                      style={{
                        ...styles.disclaimerText1,
                        ...styles.remarksContainer,
                       
                      }}
                    >
                      <Text style={styles.textBold}>Comments: </Text>
                      {state.graphNotes?.[person.name]?.["Surplus & Deficit"] ||
                        ""}
                    </Text>
                  )}
                </View>
              </PageWrapper>
            )}
          </>
        );
      })}

      {details.length > 1 && combinedIncExpdChartImages[details[0].name] && (
        <PageWrapper
          size="A4"
          style={styles.page}
          orientation="landscape"
          tradingName={tradingName}
          primaryColor={primaryColor}
          headerTitle={`Combined Income & Expenditure Graph`}
          headerSubText={""}
          footerText={regulatoryText}
        >
          <View
            style={{ ...styles.section, gap: 0, backgroundColor: "#ffffff" }}
          >
            <Text style={styles.disclaimerText1}>
              This graph combines the income and spending of both individuals,
              highlighting any surplus (excess funds) or deficit (shortfall)
              over time.
            </Text>
            <Image
              style={styles.chartImage}
              src={combinedIncExpdChartImages[details[0].name]}
            />
          </View>
        </PageWrapper>
      )}

      {details.length > 1 && combinedSurDefChartImages[details[0].name] && (
        <PageWrapper
          size="A4"
          style={styles.page}
          orientation="landscape"
          tradingName={tradingName}
          primaryColor={primaryColor}
          headerTitle={`Combined Surplus & Deficit Graph
`}
          headerSubText={""}
          footerText={regulatoryText}
        >
          <View
            style={{ ...styles.section, gap: 10, backgroundColor: "#ffffff" }}
          >
            <Text style={styles.disclaimerText1}>
              This graph shows the combined surplus and deficit over time,
              highlighting periods of extra funds or shortfalls.
            </Text>
            <Image
              style={styles.chartImage}
              src={combinedSurDefChartImages[details[0].name]}
            />
          </View>
        </PageWrapper>
      )}

      {details.length > 1 && stackedPenChartImages[details[0].name] && (
        <PageWrapper
          size="A4"
          style={styles.page}
          orientation="landscape"
          tradingName={tradingName}
          primaryColor={primaryColor}
          headerTitle={`Stacked Pensions Graph
`}
          headerSubText={""}
          footerText={regulatoryText}
        >
          <View
            style={{ ...styles.section, gap: 10, backgroundColor: "#ffffff" }}
          >
            <Text style={styles.disclaimerText1}>
              This graph stacks all pensions for both individuals, showing their
              combined details over time.
            </Text>
            <Image
              style={styles.chartImage}
              src={stackedPenChartImages[details[0].name]}
            />
          </View>
        </PageWrapper>
      )}
      {details.length > 1 && stackedInvChartImages[details[0].name] && (
        <PageWrapper
          size="A4"
          style={styles.page}
          orientation="landscape"
          tradingName={tradingName}
          primaryColor={primaryColor}
          headerTitle={`Stacked Investments Graph`}
          headerSubText={`

            `}
          footerText={regulatoryText}
        >
          <View
            style={{ ...styles.section, gap: 10, backgroundColor: "#ffffff" }}
          >
            <Text style={styles.disclaimerText1}>
              This graph stacks all investments for both individuals, showing
              their combined details over time.
            </Text>
            <Image
              style={styles.chartImage}
              src={stackedInvChartImages[details[0].name]}
            />
          </View>
        </PageWrapper>
      )}

      {/* Tables at the end */}
      {includeTables && pensionResults.length > 0 && (
        <PensionResultsSection
          pensionResults={pensionResults}
          primaryColor={primaryColor}
          details={details}
          tradingName={tradingName}
          footerText={regulatoryText}
        />
      )}

      {includeTables && investmentResults.length > 0 && (
        <InvestmentResultsSection
          investmentResults={investmentResults}
          primaryColor={primaryColor}
          details={details}
          tradingName={tradingName}
          footerText={regulatoryText}
        />
      )}

      {includeTables && incomeResults.length > 0 && (
        <IncomeExpenditureResultsSection
          incomeResults={incomeResults}
          primaryColor={primaryColor}
          details={details}
          tradingName={tradingName}
          footerText={regulatoryText}
        />
      )}
    </Document>
  );
};

export default CfmReport;
